import { FC, RefObject, useCallback } from 'react';

import { useRecoilState, useSetRecoilState } from 'recoil';

import {
  GiftAddressRegion,
  GiftOrderDeliveryMode,
} from '@shared/api/giftOrder/giftOrder.interface';
import Modal from '@shared/components/Modal';
import { TextField } from '@shared/components/TextField';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { formatPrice } from '@shared/utils/formatPrice';
import { regionFromDaumAddress } from '@shared/utils/giftAddressRegion';
import type { Address } from 'react-daum-postcode';
import DaumPostcode from 'react-daum-postcode';

import { GiftDeliveryFeePreviewState } from '../hooks/useGiftDeliveryFeePreview';
import { GiftDeliveryAddressState } from '../state/giftDeliveryAddress.atom';
import { isFreeBaseFeeApplied } from '../utils/giftDeliveryFeePreview.util';
import GiftDeliveryZoneGuideModal from './GiftDeliveryZoneGuideModal';

const ADDRESS_MODAL_ID = 'giftDeliveryAddressSearch';
const ZONE_GUIDE_MODAL_ID = 'giftDeliveryZoneGuide';

interface IProps {
  deliveryMode: GiftOrderDeliveryMode;
  onDeliveryModeChange: (mode: GiftOrderDeliveryMode) => void;
  receiverAddress: string;
  onReceiverAddressChange: (address: string) => void;
  receiverPostalCode: string;
  onReceiverPostalCodeChange: (postalCode: string) => void;
  receiverAddressDetail: string;
  onReceiverAddressDetailChange: (detail: string) => void;
  /** 카카오 응답 원문(시도·읍면동 등) 전달 — 권역 배송비 판정용. 주소 검색 완료 시 함께 갱신 */
  onReceiverRegionChange?: (region: GiftAddressRegion | undefined) => void;
  /** 권역 배송비 미리보기 상태 — 주소 아래에 배송 가능/배송비/불가 사유를 표시 */
  deliveryFee?: GiftDeliveryFeePreviewState;
  /** 개별배송(Individual) 선택 허용 여부. false면 일괄배송 전용으로 개별 전달 옵션을 숨김 */
  allowIndividual?: boolean;
  /** 주소 검색 버튼 ref — 배송지 미입력 alert 시 해당 위치로 스크롤 */
  addressButtonRef?: RefObject<HTMLButtonElement>;
  /** 최근 배송지(localStorage 보존값) — 칩 표시용 */
  savedAddress?: GiftDeliveryAddressState | null;
  /** 최근 배송지 적용 칩 노출 여부 */
  showSavedChip?: boolean;
  /** 최근 배송지 적용 콜백 */
  onApplySaved?: () => void;
  /** 배송정보 초기화 버튼 노출 여부 */
  showClear?: boolean;
  /** 배송정보 초기화 콜백 (입력값 + 저장값 모두 비움) */
  onClear?: () => void;
}

const GiftDeliveryModeSelector: FC<IProps> = ({
  deliveryMode,
  onDeliveryModeChange,
  receiverAddress,
  onReceiverAddressChange,
  receiverPostalCode,
  onReceiverPostalCodeChange,
  receiverAddressDetail,
  onReceiverAddressDetailChange,
  onReceiverRegionChange,
  deliveryFee,
  allowIndividual = true,
  addressButtonRef,
  savedAddress,
  showSavedChip = false,
  onApplySaved,
  showClear = false,
  onClear,
}) => {
  const [, setAddressModalOpen] = useRecoilState(simpleOpenAtomFamily(ADDRESS_MODAL_ID));
  const setZoneGuideOpen = useSetRecoilState(simpleOpenAtomFamily(ZONE_GUIDE_MODAL_ID));

  const handleAddressComplete = useCallback(
    (data: Address) => {
      const fullAddress =
        data.address + (data.buildingName ? ` (${data.buildingName})` : '');
      onReceiverAddressChange(fullAddress);
      onReceiverPostalCodeChange(data.zonecode);
      // 도로명 문자열엔 동이 없어 서버가 읍면동을 알 수 없다 → 카카오 원문을 그대로 실어 보낸다
      onReceiverRegionChange?.(regionFromDaumAddress(data));
      setAddressModalOpen(false);
      history.back();
    },
    [
      onReceiverAddressChange,
      onReceiverPostalCodeChange,
      onReceiverRegionChange,
      setAddressModalOpen,
    ],
  );

  const preview = deliveryFee?.preview;
  const zoneBrandId = preview?.applicable ? preview.brand._id : undefined;

  // 주소 아래 권역 배송비 안내 줄
  const renderDeliveryFeeNotice = () => {
    if (!deliveryFee || !receiverAddress) return null;
    if (deliveryFee.loading && !preview) {
      return (
        <p className="text-xs text-gray-400">배송 가능 여부와 배송비를 확인하는 중…</p>
      );
    }
    if (deliveryFee.error) {
      // 네트워크/5xx는 재시도 버튼을 붙인다. 구서버(404)는 다시 눌러도 같은 결과라 숨김
      return (
        <p className="text-xs text-gray-400">
          배송비를 확인하지 못했습니다. 결제 단계에서 다시 확인됩니다.
          {!deliveryFee.unsupported && (
            <button
              type="button"
              onClick={deliveryFee.refresh}
              className="ml-1.5 font-bold underline underline-offset-2 theme-text-main"
            >
              다시 확인
            </button>
          )}
        </p>
      );
    }
    if (!preview || !preview.applicable) return null;
    if (!preview.deliverable) {
      const incomplete = preview.reason === 'ADDRESS_INCOMPLETE';
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-600">
          <p className="break-keep font-bold">
            {incomplete ? '주소를 다시 검색해 주세요.' : '배송이 불가능한 지역입니다.'}
          </p>
          <p className="mt-0.5 break-keep">{preview.message}</p>
          <button
            type="button"
            onClick={() => setAddressModalOpen(true)}
            className="mt-1.5 rounded border border-red-300 bg-white px-2.5 py-1 font-bold text-red-600"
          >
            {incomplete ? '주소 다시 검색' : '다른 배송지 검색'}
          </button>
        </div>
      );
    }
    const freeApplied = isFreeBaseFeeApplied(preview);
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-2.5 text-xs text-green-700">
        <p className="font-bold">
          배송 가능 지역
          {preview.zone ? ` · ${preview.zone.sigungu} ${preview.zone.emd}` : ''}
          {deliveryFee.loading && (
            <span className="ml-1 font-normal text-gray-400">(재확인 중)</span>
          )}
        </p>
        <p className="mt-0.5">
          배송비 {formatPrice(preview.total)}원
          {preview.total > 0 && (
            <span className="text-green-600">
              {' '}
              (기본 {formatPrice(preview.baseFee)}원
              {preview.extraFee > 0
                ? ` + 지역 추가 ${formatPrice(preview.extraFee)}원`
                : ''}
              )
            </span>
          )}
          {freeApplied && preview.rule && (
            <span className="text-green-600">
              {' '}
              — {formatPrice(preview.rule.freeOverAmount)}원 이상 무료배송 적용
            </span>
          )}
        </p>
        {!freeApplied && preview.rule && preview.baseFee > 0 && (
          <p className="mt-0.5 text-green-600">
            {formatPrice(preview.rule.freeOverAmount)}원 이상 주문 시 기본배송비 무료
            {preview.extraFee > 0 ? ' (지역 추가배송비는 유지)' : ''}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white p-4">
      <h3 className="mb-3 font-bold">배송 상품 수령 방식</h3>

      {allowIndividual ? (
        // 개별/일괄 둘 다 가능 → 선택형 라디오
        <div className="space-y-2">
          {/* 직접 수령 (Bulk) */}
          <label
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
              deliveryMode === GiftOrderDeliveryMode.Bulk
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              name="deliveryMode"
              checked={deliveryMode === GiftOrderDeliveryMode.Bulk}
              onChange={() => onDeliveryModeChange(GiftOrderDeliveryMode.Bulk)}
              className="mt-0.5 accent-red-500"
            />
            <div className="flex-1">
              <p className="text-sm font-bold">직접 수령 (일괄배송)</p>
              <p className="mt-0.5 text-xs text-gray-500">
                입력한 배송지로 전체 수량이 배송됩니다.
              </p>
            </div>
          </label>

          {/* 개별 전달 (Individual) */}
          <label
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
              deliveryMode === GiftOrderDeliveryMode.Individual
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              name="deliveryMode"
              checked={deliveryMode === GiftOrderDeliveryMode.Individual}
              onChange={() => onDeliveryModeChange(GiftOrderDeliveryMode.Individual)}
              className="mt-0.5 accent-red-500"
            />
            <div className="flex-1">
              <p className="text-sm font-bold">개별 전달 (링크 전달)</p>
              <p className="mt-0.5 break-keep text-xs text-gray-500">
                결제 후 수령인에게 링크를 보내면, 수령인이 직접 배송지를 입력합니다.
              </p>
            </div>
          </label>
        </div>
      ) : (
        // 일괄배송 전용 → 선택지 없이 단일 안내
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-bold">직접 수령 (일괄배송)</p>
          <p className="mt-0.5 break-keep text-xs text-gray-500">
            입력하신 배송지로 전체 수량이 일괄배송됩니다.
          </p>
        </div>
      )}

      {/* Bulk 선택 시 배송지 입력 폼 */}
      {deliveryMode === GiftOrderDeliveryMode.Bulk && (
        <div className="mt-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-gray-700">배송지 정보</h4>
            {showClear && onClear && (
              <button
                type="button"
                onClick={onClear}
                className="text-xs text-gray-400 underline underline-offset-2"
              >
                초기화
              </button>
            )}
          </div>

          {/* 최근 배송지 적용 칩 — 저장값이 있고 현재 입력과 다를 때만 */}
          {showSavedChip && savedAddress && onApplySaved && (
            <button
              type="button"
              onClick={onApplySaved}
              className="flex w-full items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-left text-xs text-red-600"
            >
              <span className="flex-shrink-0">📍</span>
              <span className="flex-shrink-0 font-bold">최근 배송지 적용</span>
              <span className="truncate text-red-400">
                ({savedAddress.postalCode}) {savedAddress.address}
              </span>
            </button>
          )}

          {/* 주소 검색 */}
          <button
            ref={addressButtonRef}
            type="button"
            onClick={() => setAddressModalOpen(true)}
            className="flex w-full items-center justify-between rounded border border-gray-300 bg-white px-3 py-2.5 text-left text-sm"
          >
            {receiverAddress ? (
              <span className="text-gray-900">
                ({receiverPostalCode}) {receiverAddress}
              </span>
            ) : (
              <span className="text-gray-400">주소 검색</span>
            )}
            <svg
              className="h-4 w-4 flex-shrink-0 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>

          {/* 권역 배송비 안내 (권역 브랜드 상품이 있을 때만 내용이 생김) */}
          {renderDeliveryFeeNotice()}

          {/* 상세주소 */}
          <TextField
            scrollOnFocus={true}
            className="w-full"
            placeholder="상세주소 입력"
            value={receiverAddressDetail}
            onChange={(e) => onReceiverAddressDetailChange(e.target.value)}
          />

          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-gray-400">
              * 수령인 정보는 상단 주문자 정보가 사용됩니다.
            </p>
            {zoneBrandId && (
              <button
                type="button"
                onClick={() => setZoneGuideOpen(true)}
                className="flex-shrink-0 text-xs font-bold underline underline-offset-2 theme-text-main"
              >
                배송 가능 지역·배송비 확인
              </button>
            )}
          </div>
        </div>
      )}

      {/* 배송권역 안내 시트 (권역 브랜드일 때만) */}
      {zoneBrandId && (
        <GiftDeliveryZoneGuideModal
          openId={ZONE_GUIDE_MODAL_ID}
          giftItemBrand={zoneBrandId}
        />
      )}

      {/* 쿠폰형 상품 안내 */}
      <p className="mt-3 text-xs text-gray-400">
        * 쿠폰형 상품은 결제 후 온라인으로 전달할 수 있습니다.
      </p>

      {/* 주소 검색 모달 */}
      <Modal openId={ADDRESS_MODAL_ID}>
        <div className="p-2">
          <h3 className="mb-2 text-center font-bold">주소 검색</h3>
          <DaumPostcode style={{ height: 480 }} onComplete={handleAddressComplete} />
        </div>
      </Modal>
    </div>
  );
};

export default GiftDeliveryModeSelector;
